import type { Result, AppError } from '../../error/types';
import { isEnvironment } from './isEnvironment';
import { ENVIRONMENTS } from '../constants/ENVIRONMENTS';

/**
 * @group Functions
 * @summary <blockquote>Utility function to check if the current environment is preview using the Result pattern.</blockquote>
 * 
 * @returns Result containing true if the current environment is 'preview', false otherwise, or AppError if environment detection fails
 * 
 * @example
 * ```typescript
 * import { isPreview } from '@shared/utils';
 * 
 * const result = isPreview();
 * 
 * result.match({
 *   ok: (isPrev) => {
 *     if (isPrev) {
 *       console.log('Running in preview mode');
 *     }
 *   },
 *   err: (error) => {
 *     console.error(`Environment check failed: ${error.errorMessage}`);
 *   }
 * });
 * ```
 * 
 * @example
 * ```typescript
 * // With environment variables:
 * // VERCEL_ENV=preview → returns Ok(true)
 * // VERCEL_ENV=production → returns Ok(false)
 * // NODE_ENV=development → returns Ok(false)
 * ```
 */
export function isPreview(): Result<boolean, AppError> {
  return isEnvironment(ENVIRONMENTS.PREVIEW);
}